import React, { useEffect, useRef, useState } from 'react';
 
const ParticleText = () => {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const particlesRef = useRef([]);
  const mouseRef = useRef({ x: 0, y: 0 });
  const isHoveringRef = useRef(false);
  const animationRef = useRef(null);
 
  class Particle {
    constructor(x, y) {
      this.x = x;
      this.y = y;
      this.baseX = x;
      this.baseY = y;
      this.size = 1;
      this.vx = (Math.random() - 0.5) * 1;
      this.vy = (Math.random() - 0.5) * 1;
      this.hoverDelay = Math.random() * 20;
    }
 
    update(mouseX, mouseY, isHovering) {
      this.vx += (Math.random() - 0.5) * 0.05;
      this.vy += (Math.random() - 0.5) * 0.05;
 
      this.vx *= 0.92;
      this.vy *= 0.92;
 
      if (isHovering) {
        this.hoverDelay--;
       
        if (this.hoverDelay <= 0) {
          const dx = this.x - mouseX;
          const dy = this.y - mouseY;
          const distance = Math.sqrt(dx * dx + dy * dy);
 
          if (distance < 80) {
            const angle = Math.atan2(dy, dx);
            const force = (80 - distance) / 80 * 0.8;
            this.vx += Math.cos(angle) * force;
            this.vy += Math.sin(angle) * force;
          }
        }
      } else {
        this.hoverDelay = Math.random() * 20;
        this.vx += (this.baseX - this.x) * 0.008;
        this.vy += (this.baseY - this.y) * 0.008;
      }
 
      this.x += this.vx;
      this.y += this.vy;
    }
 
    draw(ctx) {
      ctx.fillStyle = '#1c9d90';
      ctx.globalAlpha = 0.35;
      ctx.beginPath();
      ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
      ctx.fill();
    }
  }
 
  const createParticlesFromText = (canvas) => {
    particlesRef.current = [];
 
    const tempCanvas = document.createElement('canvas');
    tempCanvas.width = 2000;
    tempCanvas.height = 300;
    const tempCtx = tempCanvas.getContext('2d');
 
    const text = 'CARESATHOME';
    const fontSize = 180;
    tempCtx.font = `700 ${fontSize}px Urbanist, sans-serif`;
    tempCtx.fillStyle = '#12534d';
    tempCtx.textAlign = 'center';
    tempCtx.textBaseline = 'middle';
    tempCtx.fillText(text, tempCanvas.width / 2, tempCanvas.height / 2);
 
    const imageData = tempCtx.getImageData(0, 0, tempCanvas.width, tempCanvas.height);
    const data = imageData.data;
 
    const offsetX = (canvas.width - tempCanvas.width) / 2;
    const offsetY = (canvas.height - tempCanvas.height) / 2;
 
    for (let i = 0; i < data.length; i += 16) {
      if (data[i + 3] > 100) {
        const pixelIndex = i / 4;
        const x = (pixelIndex % tempCanvas.width) + offsetX;
        const y = Math.floor(pixelIndex / tempCanvas.width) + offsetY;
        particlesRef.current.push(new Particle(x, y));
      }
    }
  };
 
  const initCanvas = (canvas) => {
    const tempCanvas = document.createElement('canvas');
    tempCanvas.width = 2000;
    tempCanvas.height = 300;
    const tempCtx = tempCanvas.getContext('2d');
 
    const fontSize = 180;
    tempCtx.font = `700 ${fontSize}px Urbanist, sans-serif`;
    tempCtx.fillStyle = '#12534d';
    tempCtx.textAlign = 'center';
    tempCtx.textBaseline = 'middle';
    tempCtx.fillText('CaresAtHome', tempCanvas.width / 2, tempCanvas.height / 2);
 
    const imageData = tempCtx.getImageData(0, 0, tempCanvas.width, tempCanvas.height);
    const data = imageData.data;
 
    let minX = tempCanvas.width, maxX = 0, minY = tempCanvas.height, maxY = 0;
 
    for (let i = 0; i < data.length; i += 4) {
      if (data[i + 3] > 100) {
        const pixelIndex = i / 4;
        const x = pixelIndex % tempCanvas.width;
        const y = Math.floor(pixelIndex / tempCanvas.width);
       
        minX = Math.min(minX, x);
        maxX = Math.max(maxX, x);
        minY = Math.min(minY, y);
        maxY = Math.max(maxY, y);
      }
    }
 
    const canvasWidth = Math.ceil(maxX - minX) + 500;
    const canvasHeight = Math.ceil(maxY - minY) + 80;
 
    canvas.width = canvasWidth;
    canvas.height = canvasHeight;
 
    createParticlesFromText(canvas);
  };
 
  const animate = (canvas, ctx) => {
    ctx.clearRect(0, 0, canvas.width, canvas.height);
 
    particlesRef.current.forEach(particle => {
      particle.update(mouseRef.current.x, mouseRef.current.y, isHoveringRef.current);
      particle.draw(ctx);
    });
 
    ctx.globalAlpha = 0.1;
    animationRef.current = requestAnimationFrame(() => animate(canvas, ctx));
  };
 
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
 
    const ctx = canvas.getContext('2d');
    initCanvas(canvas);
    animate(canvas, ctx);
 
    // Canvas mouseenter handler
    const handleCanvasEnter = () => {
      isHoveringRef.current = true;
    };
 
    // Canvas mouseleave handler
    const handleCanvasLeave = () => {
      isHoveringRef.current = false;
    };
 
    // Canvas mousemove handler
    const handleCanvasMove = (e) => {
      const rect = canvas.getBoundingClientRect();
      mouseRef.current.x = e.clientX - rect.left;
      mouseRef.current.y = e.clientY - rect.top;
    };
 
    // Document mouseleave handler (backup)
    const handleDocumentLeave = () => {
      isHoveringRef.current = false;
    };
 
    canvas.addEventListener('mouseenter', handleCanvasEnter);
    canvas.addEventListener('mouseleave', handleCanvasLeave);
    canvas.addEventListener('mousemove', handleCanvasMove);
    document.addEventListener('mouseleave', handleDocumentLeave);
 
    return () => {
      canvas.removeEventListener('mouseenter', handleCanvasEnter);
      canvas.removeEventListener('mouseleave', handleCanvasLeave);
      canvas.removeEventListener('mousemove', handleCanvasMove);
      document.removeEventListener('mouseleave', handleDocumentLeave);
      cancelAnimationFrame(animationRef.current);
    };
  }, []);
 
  return (
    <div
      ref={containerRef}
      style={{
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: 'Urbanist, sans-serif',
        overflow: 'hidden',
      }}
    >
      <canvas
        ref={canvasRef}
        style={{
          display: 'block',
          cursor: 'pointer',
        }}
      />
      <div
        style={{
          position: 'fixed',
          bottom: '30px',
          left: '50%',
          transform: 'translateX(-50%)',
          color: 'rgba(255, 255, 255, 0.6)',
          fontSize: '14px',
          textAlign: 'center',
          zIndex: 10,
          pointerEvents: 'none',
        }}
      >
       
      </div>
    </div>
  );
};
 
export default ParticleText;